import { HeartScribble } from "./Logo";

const dateSpot1 =
    "https://images.unsplash.com/photo-1769775555435-206cb7b9e8c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxjb3p5JTIwcm9tYW50aWMlMjByZXN0YXVyYW50JTIwZXZlbmluZ3xlbnwwfHx8fDE3Nzg0ODY4MDV8MA&ixlib=rb-4.1.0&q=85";

export default function Features() {
    return (
        <section
            id="features"
            className="relative py-28 lg:py-36 bg-[var(--paper-2)] border-y-2 border-[var(--ink)]"
            data-testid="features-section"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="reveal flex items-end justify-between flex-wrap gap-6 mb-14">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-4">
                            Sect. B — Features
                        </div>
                        <h2 className="font-display text-6xl lg:text-9xl leading-[0.85] tracking-tighter max-w-3xl">
                            The{" "}
                            <span className="font-italic-serif text-[var(--hot)]">
                                good
                            </span>{" "}
                            stuff.
                        </h2>
                    </div>
                    <div className="font-hand text-2xl text-[var(--hot-dark)] max-w-xs">
                        no swiping. no algorithmic dopamine loops. no bots.
                    </div>
                </div>

                {/* Editorial multi-column layout */}
                <div className="grid grid-cols-12 gap-5 lg:gap-7">
                    {/* Big feature — 5 curated matches */}
                    <div
                        className="reveal col-span-12 lg:col-span-8 relative bg-[var(--paper)] border-2 border-[var(--ink)] p-8 lg:p-12 overflow-hidden"
                        data-testid="feature-curated-matches"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <span className="stamp">№ 01</span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
                                Quality, always
                            </span>
                        </div>
                        <h3 className="font-display text-5xl lg:text-7xl leading-[0.9] tracking-tight">
                            Five matches.{" "}
                            <span className="font-italic-serif text-[var(--hot)]">
                                Every Monday.
                            </span>{" "}
                            That's it.
                        </h3>
                        <p className="mt-6 text-[var(--ink-soft)] max-w-xl leading-relaxed">
                            We analyse compatibility across 12 dimensions of who
                            you actually are — values, love languages, conflict
                            style, life pace — and deliver five carefully chosen
                            humans, hand-picked. Then we leave you alone.
                        </p>
                        <div className="mt-8 flex items-center gap-2">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <div
                                    key={i}
                                    className="w-12 h-12 border-2 border-[var(--ink)] flex items-center justify-center bg-[var(--paper-2)]"
                                >
                                    <HeartScribble
                                        className="w-5 h-5"
                                        color="var(--hot)"
                                    />
                                </div>
                            ))}
                            <div className="ml-3 font-hand text-xl text-[var(--ink)]">
                                ← your week.
                            </div>
                        </div>
                    </div>

                    {/* Anti-ghosting */}
                    <div
                        className="reveal col-span-12 sm:col-span-6 lg:col-span-4 relative bg-[var(--ink)] text-[var(--paper)] p-8 lg:p-10 overflow-hidden"
                        data-testid="feature-anti-ghosting"
                    >
                        <div className="text-6xl mb-6 font-display">!?</div>
                        <h3 className="font-display text-4xl leading-tight">
                            Ghosting{" "}
                            <span className="font-italic-serif text-[var(--hot)]">
                                isn't tolerated.
                            </span>
                        </h3>
                        <p className="mt-4 text-sm leading-relaxed text-[var(--paper-2)] opacity-90">
                            Gentle nudges keep the conversation flowing. Karma
                            rewards people who actually show up. Ghosts lose
                            visibility — quietly.
                        </p>
                        <div className="absolute -bottom-8 -right-8 font-display text-[8rem] text-[var(--hot)] opacity-20 leading-none">
                            ❦
                        </div>
                    </div>

                    {/* AI date spots */}
                    <div
                        className="reveal col-span-12 sm:col-span-6 lg:col-span-5 relative border-2 border-[var(--ink)] overflow-hidden group"
                        data-testid="feature-ai-date-spots"
                    >
                        <div className="relative h-72 lg:h-80">
                            <img
                                src={dateSpot1}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                                style={{ filter: "saturate(0.7) contrast(1.05)" }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/85 via-[var(--ink)]/30 to-transparent" />
                            <div className="absolute top-4 left-4">
                                <span className="stamp bg-[var(--paper)] text-[var(--ink)]">
                                    AI-curated
                                </span>
                            </div>
                            <div className="absolute bottom-0 inset-x-0 p-6 text-[var(--paper)]">
                                <h3 className="font-display text-4xl leading-tight">
                                    Date spots, sent.
                                </h3>
                                <p className="mt-2 text-sm opacity-90 max-w-xs">
                                    Venue picks tuned to your shared interests,
                                    your neighborhood, and tonight's weather.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Verification */}
                    <div
                        className="reveal col-span-12 sm:col-span-6 lg:col-span-3 relative bg-[var(--paper)] border-2 border-[var(--ink)] p-8 flex flex-col justify-between"
                        data-testid="feature-photo-verification"
                    >
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--hot)] mb-3">
                                № 04 · Verified
                            </div>
                            <h3 className="font-display text-3xl leading-tight">
                                No catfish.{" "}
                                <span className="font-italic-serif text-[var(--hot)]">
                                    Ever.
                                </span>
                            </h3>
                        </div>
                        <div className="mt-6 flex items-center gap-3">
                            <div className="w-14 h-14 border-2 border-[var(--hot)] rounded-full flex items-center justify-center text-2xl text-[var(--hot)]">
                                ✓
                            </div>
                            <div className="font-hand text-base text-[var(--ink-soft)]">
                                photo-verified humans only.
                            </div>
                        </div>
                    </div>

                    {/* Privacy mini */}
                    <div
                        className="reveal col-span-12 sm:col-span-6 lg:col-span-5 relative bg-[var(--paper)] border-2 border-[var(--ink)] p-8 lg:p-10"
                        data-testid="feature-privacy-mini"
                    >
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-soft)] mb-3">
                            № 06 · Privacy
                        </div>
                        <h3 className="font-display text-4xl leading-tight">
                            Your name stays{" "}
                            <span className="font-italic-serif text-[var(--hot)]">
                                yours.
                            </span>
                        </h3>
                        <p className="mt-4 text-sm leading-relaxed text-[var(--ink-soft)]">
                            Names are hidden until the first real-world date. We
                            never sell your story. GDPR by default.
                        </p>
                        <div className="mt-6 flex gap-2 flex-wrap">
                            {["GDPR", "No resale", "Photo-verified", "Encrypted"].map(
                                (t) => (
                                    <span
                                        key={t}
                                        className="font-mono text-[10px] uppercase tracking-[0.2em] border border-[var(--ink)] px-2 py-1"
                                    >
                                        {t}
                                    </span>
                                ),
                            )}
                        </div>
                    </div>

                    {/* Coming soon */}
                    <div
                        className="reveal col-span-12 lg:col-span-3 relative border-2 border-dashed border-[var(--ink)] p-8 flex flex-col justify-between"
                        data-testid="feature-more-coming"
                    >
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-soft)] mb-3">
                                Roadmap
                            </div>
                            <h3 className="font-display text-3xl leading-tight">
                                More on the way.
                            </h3>
                        </div>
                        <div className="mt-5 font-hand text-lg text-[var(--hot)] leading-tight">
                            voice notes · IRL events · friend-of-friend intros
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
